import { UserOutlined } from '@ant-design/icons';
import { FormHandler, FormItem, TextInput } from '@framework/form';
import { t } from '@framework/i18n';
import { DividerTitleElement, RowElement } from '@framework/ui';
import { useMemo } from 'react';
import {
  formatLegalEntityAddress,
  getLegalEntityStatusLabel,
  getLegalEntityTypeLabel,
} from './legalEntityDisplay';
import { LegalEntityProfileDisplaySchema } from './LegalEntityProfileDisplaySchema';
import type { SelectedLegalEntity } from './useSelectedLegalEntity';

export interface ILegalEntityProfileSectionProps {
  entity: SelectedLegalEntity;
  operator: {
    nome?: string | null;
    cognome?: string | null;
    email?: string | null;
  };
}

/**
 * Vista di sola lettura del Profilo quando l'utente opera per conto di una PG:
 * mostra i dati della Persona Giuridica in sessione e, in secondo piano, i dati
 * dell'operatore (Persona Fisica). I dati operatore non sono modificabili da
 * qui: la modifica del proprio profilo resta disponibile nella modalità PF.
 *
 * Il layout riusa lo stesso linguaggio grafico del Profilo PF (sezioni con
 * `DividerTitleElement` e campi `FormItem`/`TextInput` in sola lettura) per
 * mantenere le due pagine coerenti.
 */
export function LegalEntityProfileSection(
  props: ILegalEntityProfileSectionProps,
) {
  const { entity, operator } = props;

  const initialValues = useMemo(
    () => ({
      ragioneSociale: entity.ragioneSociale ?? '',
      partitaIva: entity.partitaIva ?? '',
      tipo: getLegalEntityTypeLabel(entity.tipoPg) ?? '',
      stato: getLegalEntityStatusLabel(entity.statoAccreditamento) ?? '',
      pec: entity.pec ?? '',
      sedeLegale: formatLegalEntityAddress(entity) ?? '',
      operatorEmail: operator.email ?? '',
      operatorNome: operator.nome ?? '',
      operatorCognome: operator.cognome ?? '',
    }),
    [entity, operator],
  );

  return (
    <FormHandler
      schema={LegalEntityProfileDisplaySchema}
      onSubmit={() => undefined}
      initialValues={initialValues}
    >
      <DividerTitleElement
        orientation="center"
        color="secondary"
        title={
          <b>
            {t(
              'user.profile.legal-entity.section-title',
              'Dati Persona Giuridica',
            )}
          </b>
        }
      />

      <RowElement>
        <FormItem
          name="ragioneSociale"
          readonly
          label={t(
            'user.profile.legal-entity.business-name',
            'Ragione sociale',
          )}
        >
          <TextInput name="ragioneSociale" readOnly />
        </FormItem>
        <FormItem
          name="partitaIva"
          readonly
          label={t('user.profile.legal-entity.vat-number', 'P.IVA')}
        >
          <TextInput name="partitaIva" readOnly />
        </FormItem>
      </RowElement>

      <RowElement>
        <FormItem
          name="tipo"
          readonly
          label={t('user.profile.legal-entity.type', 'Tipo')}
        >
          <TextInput name="tipo" readOnly />
        </FormItem>
        <FormItem
          name="stato"
          readonly
          label={t('user.profile.legal-entity.status', 'Stato')}
        >
          <TextInput name="stato" readOnly />
        </FormItem>
      </RowElement>

      <FormItem
        name="pec"
        readonly
        label={t('user.profile.legal-entity.pec', 'PEC')}
      >
        <TextInput name="pec" readOnly />
      </FormItem>

      <FormItem
        name="sedeLegale"
        readonly
        label={t('user.profile.legal-entity.registered-office', 'Sede legale')}
      >
        <TextInput name="sedeLegale" readOnly />
      </FormItem>

      <DividerTitleElement
        orientation="center"
        color="secondary"
        title={
          <b>{t('user.profile.operator.section-title', 'Dati operatore')}</b>
        }
      />

      <FormItem
        name="operatorEmail"
        readonly
        label={t('user.profile.operator.email', 'Email')}
      >
        <TextInput
          name="operatorEmail"
          readOnly
          prefix={
            <UserOutlined aria-hidden="true" style={{ color: '#757575' }} />
          }
        />
      </FormItem>

      <RowElement>
        <FormItem
          name="operatorNome"
          readonly
          label={t('user.profile.operator.name', 'Nome')}
        >
          <TextInput name="operatorNome" readOnly />
        </FormItem>
        <FormItem
          name="operatorCognome"
          readonly
          label={t('user.profile.operator.family-name', 'Cognome')}
        >
          <TextInput name="operatorCognome" readOnly />
        </FormItem>
      </RowElement>
    </FormHandler>
  );
}
